import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';
import { DeleteForever, Refresh, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

interface InactiveProfile {
  user_id: number;
  profile_id?: string;
  name: string;
  email: string;
  phone: string;
  review_status: string;
  created_at: string;
  can_delete_pending_profile: boolean;
}

const AdminInactiveUsersPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, userRole } = useAuthStore();
  const isSuperAdmin = (userRole || user?.role) === 'SUPER_ADMIN';
  const [filters, setFilters] = useState({ search: '', status: 'ALL', from: '', to: '', sort: 'oldest', page: 0, size: 25 });
  const [items, setItems] = useState<InactiveProfile[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);
  const requestVersion = useRef(0);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const version = ++requestVersion.current;
    setSelected([]);
    setLoading(true);
    const timer = window.setTimeout(async () => {
      if (filters.from && filters.to && filters.from > filters.to) {
        setError('Registered from cannot be later than registered to.');
        setLoading(false);
        return;
      }
      try {
        const response = await api.get('/users/admin/users', { params: {
          pending_only: true, search: filters.search.trim() || undefined, review_status: filters.status,
          registered_from: filters.from ? `${filters.from}T00:00:00` : undefined,
          registered_to: filters.to ? `${filters.to}T23:59:59.999999` : undefined,
          sort_order: filters.sort, page: filters.page + 1, page_size: filters.size,
        } });
        if (version !== requestVersion.current) return;
        setItems(response.data.items || []);
        setTotal(response.data.total || 0);
        if (response.data.page - 1 !== filters.page) {
          setFilters((previous) => ({ ...previous, page: response.data.page - 1 }));
        }
      } catch (failure: any) {
        if (version === requestVersion.current) setError(failure.response?.data?.detail || 'Failed to load inactive users');
      } finally {
        if (version === requestVersion.current) setLoading(false);
      }
    }, 250);
    return () => { window.clearTimeout(timer); requestVersion.current += 1; };
  }, [filters, refresh, isSuperAdmin]);

  const updateFilter = (key: 'search' | 'status' | 'from' | 'to' | 'sort', value: string) => {
    setSelected([]);
    setError('');
    setFilters((previous) => ({ ...previous, [key]: value, page: 0 }));
  };
  const eligibleIds = items.filter((item) => item.can_delete_pending_profile).map((item) => item.user_id);
  const deleteSelected = async () => {
    if (confirmation !== 'DELETE' || !selected.length) return;
    setDeleting(true);
    setError('');
    setResult('');
    try {
      const response = await api.post('/users/admin/pending-profiles/bulk-delete', { user_ids: selected, confirmation });
      const failures: Array<{ user_id: number; detail: string }> = response.data.failures || [];
      setResult(`${response.data.deleted_ids.length} profile(s) permanently deleted.`);
      if (failures.length) setError(failures.map((failure) => `User ${failure.user_id}: ${failure.detail}`).join(' | '));
      setDialogOpen(false);
      setConfirmation('');
      setSelected([]);
      setRefresh((previous) => previous + 1);
    } catch (failure: any) {
      setError(failure.response?.data?.detail || 'Deletion could not be confirmed. Refresh the list before retrying.');
      setDialogOpen(false);
      setSelected([]);
      setRefresh((previous) => previous + 1);
    } finally {
      setDeleting(false);
    }
  };

  return <AdminLayout currentPage="inactive-users">
    <Typography variant="h4" sx={{ fontWeight: 800, mb: 2 }}>Inactive Users</Typography>
    {!isSuperAdmin ? <Alert severity="warning">Only super admins can manage this section.</Alert> : <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: '2fr 1fr 1fr 1fr 1fr' }, gap: 1.5, mb: 2 }}>
        <TextField label="Search by ID, name, email or phone" value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} size="small" />
        <TextField select label="Profile status" value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} size="small">
          <MenuItem value="ALL">Incomplete and Pending</MenuItem><MenuItem value="INCOMPLETE">Incomplete</MenuItem><MenuItem value="PENDING">Pending</MenuItem>
        </TextField>
        <TextField type="date" label="Registered from" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(event) => updateFilter('from', event.target.value)} size="small" />
        <TextField type="date" label="Registered to" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(event) => updateFilter('to', event.target.value)} size="small" />
        <TextField select label="Registration order" value={filters.sort} onChange={(event) => updateFilter('sort', event.target.value)} size="small"><MenuItem value="oldest">Oldest first</MenuItem><MenuItem value="newest">Newest first</MenuItem></TextField>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography>{total} profiles</Typography>
        <Button startIcon={<DeleteForever />} color="error" variant="outlined" disabled={!selected.length || loading || deleting} onClick={() => { setConfirmation(''); setDialogOpen(true); }}>Delete Selected ({selected.length})</Button>
        <IconButton aria-label="Refresh inactive users" title="Refresh inactive users" disabled={deleting} onClick={() => setRefresh((previous) => previous + 1)}><Refresh /></IconButton>
        <Button disabled={deleting} onClick={() => setFilters({ search: '', status: 'ALL', from: '', to: '', sort: 'oldest', page: 0, size: 25 })}>Clear Filters</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {result && <Alert severity="success" sx={{ mb: 2 }}>{result}</Alert>}
      {loading ? <CircularProgress /> : !error || items.length > 0 ? <>
        <TableContainer sx={{ backgroundColor: '#fff' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead><TableRow>
              <TableCell padding="checkbox"><Checkbox inputProps={{ 'aria-label': 'Select eligible profiles on this page' }} checked={eligibleIds.length > 0 && selected.length === eligibleIds.length} indeterminate={selected.length > 0 && selected.length < eligibleIds.length} disabled={!eligibleIds.length || deleting} onChange={(event) => setSelected(event.target.checked ? eligibleIds : [])} /></TableCell>
              {['Profile ID', 'Name', 'Email', 'Phone', 'Status', 'Registered', 'Action'].map((label) => <TableCell key={label}>{label}</TableCell>)}
            </TableRow></TableHead>
            <TableBody>{items.map((item) => <TableRow key={item.user_id}>
              <TableCell padding="checkbox"><Checkbox inputProps={{ 'aria-label': `Select ${item.profile_id || item.user_id}` }} checked={selected.includes(item.user_id)} disabled={!item.can_delete_pending_profile || deleting} onChange={(event) => setSelected((previous) => event.target.checked ? [...previous, item.user_id] : previous.filter((id) => id !== item.user_id))} /></TableCell>
              <TableCell>{item.profile_id || item.user_id}</TableCell><TableCell>{item.name}</TableCell><TableCell sx={{ overflowWrap: 'anywhere' }}>{item.email}</TableCell><TableCell>{item.phone}</TableCell><TableCell>{item.review_status}</TableCell><TableCell>{new Date(item.created_at.endsWith('Z') ? item.created_at : `${item.created_at}Z`).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</TableCell>
              <TableCell><IconButton aria-label={`View ${item.profile_id || item.user_id}`} title="View profile" onClick={() => navigate(`/admin/profile-reviews?userId=${item.user_id}`)}><Visibility /></IconButton></TableCell>
            </TableRow>)}{!items.length && <TableRow><TableCell colSpan={8}>No inactive profiles match these filters.</TableCell></TableRow>}</TableBody>
          </Table>
        </TableContainer>
        <TablePagination component="div" count={total} page={filters.page} rowsPerPage={filters.size} rowsPerPageOptions={[25, 50, 100]} onPageChange={(_event, page) => { setSelected([]); setFilters((previous) => ({ ...previous, page })); }} onRowsPerPageChange={(event) => { setSelected([]); setFilters((previous) => ({ ...previous, size: Number(event.target.value), page: 0 })); }} />
      </> : null}
      <Dialog open={dialogOpen} onClose={() => !deleting && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Permanently Delete {selected.length} Profiles?</DialogTitle>
        <DialogContent><Alert severity="warning" sx={{ mb: 2 }}>This permanently removes the selected accounts, associated records, and local images. This cannot be undone. Ensure you have a backup.</Alert>
          <Typography sx={{ mb: 2, overflowWrap: 'anywhere' }}>{items.filter((item) => selected.includes(item.user_id)).map((item) => item.profile_id || item.user_id).join(', ')}</Typography>
          <TextField fullWidth label="Type DELETE to confirm" value={confirmation} disabled={deleting} onChange={(event) => setConfirmation(event.target.value)} />
        </DialogContent>
        <DialogActions><Button disabled={deleting} onClick={() => setDialogOpen(false)}>Cancel</Button><Button startIcon={<DeleteForever />} color="error" variant="contained" disabled={deleting || confirmation !== 'DELETE'} onClick={deleteSelected}>{deleting ? 'Deleting...' : 'Delete Permanently'}</Button></DialogActions>
      </Dialog>
    </>}
  </AdminLayout>;
};

export default AdminInactiveUsersPage;